import { SplitButton } from 'primereact/splitbutton';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmDialog } from 'primereact/confirmdialog';
import { RecomendacaoSemeaduraSimple, Safra } from '../../@types';
import {
  ItensSaldoService,
  RecomendacaoSemeaduraService,
} from '../../services';
import Agro1Loader from '../../components/Agro1Loader';
import {
  convertCultivares,
  convertToSaveableObject,
  validarTalhoes,
} from './utils';
import Agro1Toast from '../../components/Agro1Toast';
import { formatErrorMessage } from './create-edit/utils';
import usePrintRecomendacaoStore from './store';

export type ActionButtonsProps = {
  item: RecomendacaoSemeaduraSimple;
  safras: Safra[];
  listReloader?: () => Promise<void>;
};

const ActionButtons: FC<ActionButtonsProps> = ({
  item,
  safras,
  listReloader,
}) => {
  const navigate = useNavigate();
  const fetchDataToPrint = usePrintRecomendacaoStore(
    (state) => state.fetchDataToPrint,
  );

  const setPrintData = useCallback(async () => {
    const loaderId = Agro1Loader.show();
    try {
      const baseRecord = await RecomendacaoSemeaduraService.find(
        String(item.id),
      );
      await fetchDataToPrint(baseRecord, safras);
    } catch (error) {
      console.error(error);

      Agro1Toast.show({
        detail: 'Erro ao carregar dados para impressão.',
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  }, [item.id, fetchDataToPrint, safras]);

  const propsPublicarRascunho = useMemo(
    () =>
      item.status === 'publicado'
        ? { label: 'Rascunho', icon: 'pi pi-file-edit' }
        : { label: 'Publicar', icon: 'pi pi-file-export' },
    [item.status],
  );

  const updateStatus = useCallback(async () => {
    const loaderId = Agro1Loader.show();
    const baseRecord = await RecomendacaoSemeaduraService.find(String(item.id));
    const newStatus = item.status === 'publicado' ? 'rascunho' : 'publicado';

    try {
      if (!baseRecord.periodo) {
        Agro1Toast.show({
          detail: 'Falha ao atualizar o registro. Período não informado.',
          severity: 'error',
          sticky: true,
        });

        return;
      }

      const cultivares = await ItensSaldoService.saldos({
        propriedadeId: baseRecord.propriedade.id,
        operacaoItem: 'cultivar',
        noPaginate: true,
      });
      const talhoes = await convertCultivares(baseRecord, cultivares);

      await validarTalhoes(baseRecord.propriedade.id, talhoes);

      const saveableObject = await convertToSaveableObject(baseRecord);

      await RecomendacaoSemeaduraService.update({
        ...saveableObject,
        id: String(item.id),
        status: newStatus,
      });

      if (listReloader) await listReloader();

      Agro1Toast.show({
        detail: 'Registro atualizado com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      const errorMsgProps = formatErrorMessage(error, 'edit');

      Agro1Toast.show({
        ...errorMsgProps,
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  }, [item.status, item.id, listReloader]);

  const onDelete = useCallback(async () => {
    const loaderId = Agro1Loader.show();

    try {
      await RecomendacaoSemeaduraService.delete(item.id.toString());

      if (listReloader) await listReloader();

      Agro1Toast.show({
        detail: 'Registro excluído com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      const { detail } = formatErrorMessage(error, 'edit');

      Agro1Toast.show({
        detail,
        severity: 'error',
        sticky: true,
      });
      throw new Error('Erro ao tentar excluir o registro.');
    } finally {
      Agro1Loader.close(loaderId);
    }
  }, [item.id, listReloader]);

  const beforeOnDelete = useCallback(() => {
    confirmDialog({
      message: 'Você tem certeza que deseja excluir?',
      header: 'Atenção',
      icon: 'pi pi-info-circle',
      accept: onDelete,
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptClassName: 'button-danger',
      rejectClassName: 'button-primary',
    });
  }, [onDelete]);

  const items = useMemo(
    () => [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => navigate(`/recomendacoes-semeadura/edit/${item.id}`),
      },
      {
        label: 'Imprimir',
        icon: 'pi pi-print',
        command: setPrintData,
      },
      {
        label: propsPublicarRascunho.label,
        icon: propsPublicarRascunho.icon,
        command: updateStatus,
      },
      {
        label: 'Excluir',
        icon: 'pi pi-times',
        command: beforeOnDelete,
      },
    ],
    [
      item.id,
      navigate,
      propsPublicarRascunho,
      updateStatus,
      beforeOnDelete,
      setPrintData,
    ],
  );

  return (
    <SplitButton
      model={items}
      text
      dropdownIcon="pi pi-ellipsis-v"
      className="acoes"
      buttonClassName="p-0"
    />
  );
};
export default ActionButtons;
